import * as React from 'react'
import {Component, Fragment} from 'react'
import {findDOMNode} from 'react-dom'
import {inject} from 'mobx-react'
import cn from 'classnames'
import {WidgetApi} from '../../lib/WidgetApi'
import {IFormBase} from '../../lib/forms/IFormBase'
import {isFieldInvalid, isFieldRequired} from '../../lib/helpers'

///////////////////////////////////////////////////////////////////////////////

export type InputCheckableWrapperProps = {
  api?: WidgetApi
  outerStyle?: 'square' | 'circle'
  innerStyle?: 'tick' | 'circle'
  children?: any
  form?: IFormBase<any>
  fields?: string | string[]
  skipMarkRequired?: boolean
}

///////////////////////////////////////////////////////////////////////////////

export class InputCheckboxWrapper extends Component<
  InputCheckableWrapperProps,
  any
> {
  public static defaultProps: InputCheckableWrapperProps = {
    outerStyle: 'square',
    innerStyle: 'tick',
  }

  render() {
    return (
      <InputCheckableWrapper {...this.props}>
        {this.props.children}
      </InputCheckableWrapper>
    )
  }
}

///////////////////////////////////////////////////////////////////////////////

export class InputRadioWrapper extends Component<
  InputCheckableWrapperProps,
  any
> {
  public static defaultProps: InputCheckableWrapperProps = {
    outerStyle: 'circle',
    innerStyle: 'circle',
  }

  render() {
    return (
      <InputCheckableWrapper {...this.props}>
        {this.props.children}
      </InputCheckableWrapper>
    )
  }
}

///////////////////////////////////////////////////////////////////////////////

@inject('api')
class InputCheckableWrapper extends Component<InputCheckableWrapperProps, any> {
  private input: HTMLInputElement
  private formBlock: HTMLElement
  private fieldBlock: HTMLElement
  private labelBlock: HTMLElement

  constructor(props) {
    super(props)

    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onChange = this.onChange.bind(this)
  }

  componentDidMount() {
    this.input = findDOMNode(this) as HTMLInputElement
    this.formBlock = this.input.closest('.form-block') as HTMLElement
    this.fieldBlock = this.input.closest('.field-block') as HTMLElement
    this.labelBlock = this.input.closest(
      '.label-radio-block, .label-checkbox-block',
    ) as HTMLElement

    let {input, formBlock, onFocus, onBlur, onChange} = this

    if (input) {
      input.addEventListener('focus', onFocus)
      input.addEventListener('blur', onBlur)
      input.addEventListener('change', onChange)

      if (formBlock) {
        formBlock.classList.add('checkable-list-wrapper')
      }

      this.addIconError()
      this.toggleInputCustomCheckableClass()
      this.toggleInputFocusedClass()
      this.toggleInputCheckedClass()
      this.toggleHasErrorClass()
      this.toggleLabelMarkRequired()
    }
  }

  componentDidUpdate() {
    let {input} = this

    if (input) {
      this.toggleInputCustomCheckableClass()
      this.toggleInputFocusedClass()
      this.toggleInputCheckedClass()
      this.toggleHasErrorClass()
      this.toggleLabelMarkRequired()
    }
  }

  componentWillUnmount() {
    let {input, onFocus, onBlur} = this

    if (input) {
      input.removeEventListener('focus', onFocus)
      input.removeEventListener('blur', onBlur)
    }
  }

  onFocus() {
    let {labelBlock} = this

    if (labelBlock) {
      labelBlock.classList.add('input-focused')
    }
  }

  onBlur() {
    let {labelBlock} = this

    if (labelBlock) {
      labelBlock.classList.remove('input-focused')
    }
  }

  onChange() {
    this.toggleInputCheckedClass()
  }

  addIconError() {
    let {fieldBlock} = this

    if (fieldBlock) {
      let iconsBlock = fieldBlock.querySelector('.icons-block') as HTMLElement

      if (!iconsBlock) {
        iconsBlock = document.createElement('div')
        iconsBlock.classList.add('icons-block')
        fieldBlock.appendChild(iconsBlock)
      }

      let iconError = iconsBlock.querySelector('.icon-error') as HTMLElement

      if (!iconError) {
        iconError = document.createElement('div')
        iconError.classList.add('icon-error')
        iconsBlock.appendChild(iconError)
      }
    }
  }

  toggleInputCustomCheckableClass() {
    let {input} = this
    let {uiCustomCheckableElements} = this.props.api.computedConfig

    if (uiCustomCheckableElements) {
      input.classList.add('md-custom-checkable')
    } else {
      input.classList.remove('md-custom-checkable')
    }
  }

  toggleInputFocusedClass() {
    let {input} = this

    if (input) {
      if (input === document.activeElement) {
        this.onFocus()
      } else {
        this.onBlur()
      }
    }
  }

  toggleInputCheckedClass() {
    let {input, labelBlock} = this

    if (input && labelBlock) {
      if (input.checked) {
        labelBlock.classList.add('input-checked')
      } else {
        labelBlock.classList.remove('input-checked')
      }
    }
  }

  toggleHasErrorClass() {
    let {
      formBlock,
      props: {form, fields},
    } = this

    if (formBlock) {
      let {debugErrorMessages} = this.props.api.computedConfig

      if (isFieldInvalid(form, fields) || debugErrorMessages) {
        formBlock.classList.add('has-error')
      } else {
        formBlock.classList.remove('has-error')
      }
    }
  }

  toggleLabelMarkRequired() {
    let {
      input,
      labelBlock,
      props: {form, fields, skipMarkRequired},
    } = this

    if (input && labelBlock && !skipMarkRequired) {
      if (isFieldRequired(form, fields)) {
        let mark = labelBlock.querySelector('.label-mark-required')

        if (!mark) {
          mark = document.createElement('span')
          mark.classList.add('label-mark-required')
          labelBlock.appendChild(mark)
        }
      } else {
        let mark = labelBlock.querySelector('.label-mark-required')

        if (mark) {
          labelBlock.removeChild(mark)
        }
      }
    }
  }

  render() {
    let {api, children, outerStyle, innerStyle} = this.props
    let {uiCustomCheckableElements, uiCheckboxRadius} = api.computedConfig

    let wrapperClass = [
      'md-checkable-icon-wrapper',
      {
        square: outerStyle === 'square',
        circle: outerStyle === 'circle',
      },
    ]

    let iconClass = [
      'md-checkable-icon',
      {
        square: outerStyle === 'square',
        circle: outerStyle === 'circle',
      },
    ]

    return (
      <Fragment>
        {children}

        {uiCustomCheckableElements && (
          <span className={cn(wrapperClass)}>
            <svg
              className={cn(iconClass)}
              viewBox="0 0 22 22"
              xmlns="http://www.w3.org/2000/svg"
              focusable="false"
            >
              {outerStyle === 'square' && (
                <rect
                  className="md-outer-square"
                  rx={uiCheckboxRadius}
                  ry={uiCheckboxRadius}
                  x="1"
                  y="1"
                  width="20"
                  height="20"
                />
              )}

              {outerStyle === 'circle' && (
                <circle
                  className="md-outer-circle"
                  cx="10.809"
                  cy="10.809"
                  r="9.461"
                />
              )}

              {innerStyle === 'tick' && (
                <polyline
                  className="md-inner-tick"
                  points="7.2608 11.8184 10.1312 13.904 14.3016 8.164"
                />
              )}

              {innerStyle === 'circle' && (
                <circle
                  className="md-inner-circle"
                  cx="10.809"
                  cy="10.809"
                  r="2.461"
                />
              )}
            </svg>
          </span>
        )}
      </Fragment>
    )
  }
}
